import { ActivationEntryType } from "app/pages/Customer/Activation/activation-entry-types";

export const FIXED_PHONE_STORAGE_KEY = "fixedPhonePurchaseFlow";

/**
 * Which activation entry path produced checkout/session data; only stored when `flowType === "activation"`.
 * `preloaded-sim` = mobile plan service flow (preloaded SIM / SIM upsell on `/activation`).
 * `fixed-phone-base` = reseller fixed-phone hardware activation (activation code flow).
 */
export type ActivationPurchaseFlow =
  (typeof ActivationEntryType)[keyof typeof ActivationEntryType];

export interface FixedPhoneOrderSummaryData {
  enteredCoupon?: string;
  appliedCoupon?: string;
  isCouponApplied?: boolean;
  couponAppliedByCustomer?: boolean;
}

export interface FixedPhoneCheckoutData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  password?: string;
  securityQuestion?: string;
  answer?: string;
  streetAddress?: string;
  apt?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  source?: string;
  nameOnCredit?: string;
  cardNumber?: boolean;
  cardExpiry?: boolean;
  cardCvc?: boolean;
  acceptTerms?: boolean;
  trialOption?: "skip" | "subscribe";
  promoCode?: string;
  paymentMethodId?: string;
  selectedPaymentMethodType?: string;
}

export interface FixedPhonePurchaseFlowData {
  checkout?: FixedPhoneCheckoutData;
  orderSummary?: FixedPhoneOrderSummaryData;
  flowType?: "activation" | "fixed";
  activationPurchaseFlow?: ActivationPurchaseFlow;
  activation?: ActivationFlowData;
}

export interface ActivationFlowData {
  zipCode?: string;
  address?: string;
  activationCode?: string;
  monthlyPlanId?: string;
  yearlyPlanId?: string;
  planId?: string;
  selectedPlanType?: "monthly" | "yearly";
  optForSecondMonthUpsell?: boolean;
  optForSecurityPlan?: boolean;
  securityPlanId?: string;
  simData?: any;
  preloadedSimInventoryDevice?: any;
  preloadedSimDeviceForm?: {
    simNumber?: string;
    imeiNumber?: string;
  };
  preloadedSimVerifiedSimDigits?: string;
  preloadedSimVerifiedImei?: string;
  /** Set after device inventory success; used to detect edits that invalidate later steps. */
  preloadedSimDeviceCommitFingerprint?: string;
  preloadedSimPrimaryDataTier?: string;
  preloadedSimServiceFrequency?: string;
  preloadedSimPlanId?: string;
  preloadedSimOptedForDataOrFrequencyUpsell?: boolean;
  preloadedSimUpgradeCardDraft?: {
    primaryDataTier: string;
    serviceFrequency: string;
  };
}

export const getFixedPhonePurchaseFlowData =
  (): FixedPhonePurchaseFlowData | null => {
    try {
      const data = sessionStorage.getItem(FIXED_PHONE_STORAGE_KEY);
      if (!data) return null;
      return JSON.parse(data) as FixedPhonePurchaseFlowData;
    } catch (error) {
      console.error("Error reading fixed phone purchase flow data:", error);
      return null;
    }
  };

export const saveFixedPhonePurchaseFlowData = (
  data: Partial<FixedPhonePurchaseFlowData>
): void => {
  try {
    const existing = getFixedPhonePurchaseFlowData() || {};
    const updated = { ...existing };

    // Perform a shallow merge on known nested objects to prevent data loss when saving independently
    if (data.checkout !== undefined) {
      updated.checkout = { ...(existing.checkout || {}), ...data.checkout };
    }
    if (data.orderSummary !== undefined) {
      updated.orderSummary = {
        ...(existing.orderSummary || {}),
        ...data.orderSummary,
      };
    }
    if (data.flowType !== undefined) {
      updated.flowType = data.flowType;
    }
    if (data.activationPurchaseFlow !== undefined) {
      updated.activationPurchaseFlow = data.activationPurchaseFlow;
    }
    if (data.activation !== undefined) {
      updated.activation = { ...(existing.activation || {}), ...data.activation };
    }
    if (updated.flowType === "fixed") {
      delete updated.activationPurchaseFlow;
    }
    sessionStorage.setItem(FIXED_PHONE_STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error("Error saving fixed phone purchase flow data:", error);
  }
};

/** Router `state` for `/checkout` when coming from activation steps; includes purchase-flow when set in session. */
export function buildActivationCheckoutLocationState(): {
  flowType: "activation";
  from: "activation";
  activationPurchaseFlow?: ActivationPurchaseFlow;
} {
  const full = getFixedPhonePurchaseFlowData();
  const flow = full?.activationPurchaseFlow;
  return {
    flowType: "activation",
    from: "activation",
    ...(flow ? { activationPurchaseFlow: flow } : {}),
  };
}

export const getActivationFlowData = (): ActivationFlowData | null => {
  const data = getFixedPhonePurchaseFlowData();
  if (!data || data.flowType !== "activation") {
    return null;
  }
  return data.activation || null;
};

export const saveActivationFlowData = (
  data: Partial<ActivationFlowData>,
  opts?: { activationPurchaseFlow?: ActivationPurchaseFlow }
): void => {
  saveFixedPhonePurchaseFlowData({
    flowType: "activation",
    activation: data,
    ...(opts?.activationPurchaseFlow !== undefined
      ? { activationPurchaseFlow: opts.activationPurchaseFlow }
      : {}),
  });
};

export const resetActivationFlowProgress = (): void => {
  try {
    const full = getFixedPhonePurchaseFlowData();
    const existingActivation = full?.activation || {};
    const flow = full?.activationPurchaseFlow;
    const next: FixedPhonePurchaseFlowData = {
      flowType: "activation",
      activation: {
        zipCode: existingActivation.zipCode,
        address: existingActivation.address,
      },
      ...(flow ? { activationPurchaseFlow: flow } : {}),
    };
    sessionStorage.setItem(FIXED_PHONE_STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    console.error("Error resetting activation flow progress:", error);
  }
};

export const getFixedPhoneCheckoutData = (): FixedPhoneCheckoutData | null => {
  const data = getFixedPhonePurchaseFlowData();
  return data?.checkout || null;
};

export const saveFixedPhoneCheckoutData = (
  data: FixedPhoneCheckoutData
): void => {
  saveFixedPhonePurchaseFlowData({ checkout: data });
};

export const clearFixedPhoneCheckoutData = (): void => {
  const data = getFixedPhonePurchaseFlowData();
  if (data) {
    delete data.checkout;
    sessionStorage.setItem(FIXED_PHONE_STORAGE_KEY, JSON.stringify(data));
  }
};

export const clearFixedPhonePurchaseFlow = (): void => {
  try {
    sessionStorage.removeItem(FIXED_PHONE_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing fixed phone purchase flow:", error);
  }
};

export const getFixedPhoneOrderSummaryData =
  (): FixedPhoneOrderSummaryData | null => {
    const data = getFixedPhonePurchaseFlowData();
    return data?.orderSummary || null;
  };

export const saveFixedPhoneOrderSummaryData = (
  orderSummary: Partial<FixedPhoneOrderSummaryData>
): void => {
  saveFixedPhonePurchaseFlowData({ orderSummary });
};
